import { useEffect, useRef } from 'react';
import { makeStyles } from '@material-ui/core';

// Emblema Yggdrasil "tree-of-life" como grafo, desenhado em Canvas (o skill de
// design pede Canvas/WebGL para gráficos decorativos em vez de path SVG longo
// à mão). Circular e SIMÉTRICO — cada galho gera um par ±, então a simetria é
// exata, não aleatória: copa em cima, raízes espelhadas embaixo, contidas num
// anel duplo fino com um halo de contas girando devagar. As pontas dos galhos
// são nós de serviço que pulsam — a árvore É o grafo. Geometria construída uma
// vez por resize; só os nós e o halo animam. Estático sob prefers-reduced-motion.

const useStyles = makeStyles({
  canvas: {
    position: 'absolute',
    inset: 0,
    width: '100%',
    height: '100%',
  },
});

type Seg = { x1: number; y1: number; x2: number; y2: number; d: number };
type Tip = { x: number; y: number; ph: number; hot?: boolean; root?: boolean };
type Bead = { a: number; bright: boolean };

const CANOPY_D = 6;
const ROOT_D = 4;

function lerpHex(c1: string, c2: string, f: number): string {
  const a = parseInt(c1.slice(1), 16);
  const b = parseInt(c2.slice(1), 16);
  const r = Math.round((a >> 16) + (((b >> 16) - (a >> 16)) * f));
  const g = Math.round(((a >> 8) & 255) + ((((b >> 8) & 255) - ((a >> 8) & 255)) * f));
  const bl = Math.round((a & 255) + (((b & 255) - (a & 255)) * f));
  return `rgb(${r},${g},${bl})`;
}

export const AuthGraphMotif = () => {
  const classes = useStyles();
  const ref = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext('2d');
    if (!ctx) return undefined;

    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    let raf = 0;
    let t0 = 0;
    let rz: ReturnType<typeof setTimeout>;
    let W = 0;
    let H = 0;
    let cx = 0;
    let cy = 0;
    let R = 0;
    let branches: Seg[] = [];
    let roots: Seg[] = [];
    let tips: Tip[] = [];
    let halo: Bead[] = [];

    const grow = () => {
      cx = W * 0.5;
      cy = H * 0.42;
      R = Math.min(W, H) * 0.37;
      branches = [];
      roots = [];
      tips = [];
      const clamp = (x: number, y: number): [number, number] => {
        const d = Math.hypot(x, y);
        if (d > R * 0.95) {
          const k = (R * 0.95) / d;
          return [x * k, y * k];
        }
        return [x, y];
      };
      const phase = (x: number, y: number) =>
        (((x * 13.7 + y * 7.1) % 6.283) + 6.283) % 6.283;

      // tronco
      branches.push({ x1: 0, y1: R * 0.52, x2: 0, y2: R * 0.28, d: CANOPY_D });
      const br = (x: number, y: number, ang: number, len: number, d: number) => {
        const [x2, y2] = clamp(x + Math.cos(ang) * len, y + Math.sin(ang) * len);
        branches.push({ x1: x, y1: y, x2, y2, d });
        if (d <= 0) {
          tips.push({ x: x2, y: y2, ph: phase(x2, y2), hot: Math.round(x2 * 10) % 3 === 0 });
          return;
        }
        const spread = 0.3 + (CANOPY_D - d) * 0.07;
        br(x2, y2, ang - spread, len * 0.75, d - 1);
        br(x2, y2, ang + spread, len * 0.75, d - 1);
      };
      br(0, R * 0.28, -Math.PI / 2, R * 0.32, CANOPY_D);

      // base das raízes
      roots.push({ x1: 0, y1: R * 0.52, x2: 0, y2: R * 0.6, d: ROOT_D });
      const rt = (x: number, y: number, ang: number, len: number, d: number) => {
        const [x2, y2] = clamp(x + Math.cos(ang) * len, y + Math.sin(ang) * len);
        roots.push({ x1: x, y1: y, x2, y2, d });
        if (d <= 0) {
          tips.push({ x: x2, y: y2, ph: phase(x2, y2), root: true });
          return;
        }
        const spread = 0.44 + (ROOT_D - d) * 0.06;
        rt(x2, y2, ang - spread, len * 0.72, d - 1);
        rt(x2, y2, ang + spread, len * 0.72, d - 1);
      };
      rt(0, R * 0.6, Math.PI / 2, R * 0.22, ROOT_D);

      halo = Array.from({ length: 36 }, (_, i) => ({
        a: (i / 36) * 6.283,
        bright: i % 4 === 0,
      }));
    };

    const size = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      W = canvas.clientWidth;
      H = canvas.clientHeight;
      canvas.width = W * dpr;
      canvas.height = H * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      grow();
    };

    const frame = (now: number) => {
      if (!t0) t0 = now || 0;
      const t = (now || 0) - t0;
      ctx.clearRect(0, 0, W, H);
      ctx.save();
      ctx.translate(cx, cy);

      // brilho radial atrás do emblema
      const glow = ctx.createRadialGradient(0, 0, R * 0.1, 0, 0, R * 1.05);
      glow.addColorStop(0, 'rgba(219,35,40,.10)');
      glow.addColorStop(1, 'transparent');
      ctx.fillStyle = glow;
      ctx.beginPath();
      ctx.arc(0, 0, R * 1.05, 0, 7);
      ctx.fill();

      // anel duplo fino
      ctx.lineWidth = 1.2;
      ctx.strokeStyle = 'rgba(240,117,117,.26)';
      ctx.beginPath();
      ctx.arc(0, 0, R, 0, 7);
      ctx.stroke();
      ctx.lineWidth = 1;
      ctx.strokeStyle = 'rgba(144,131,209,.14)';
      ctx.beginPath();
      ctx.arc(0, 0, R * 0.9, 0, 7);
      ctx.stroke();

      // halo de contas girando devagar
      const rot = reduce ? 0 : t / 11000;
      for (const b of halo) {
        const a = b.a + rot;
        ctx.beginPath();
        ctx.arc(Math.cos(a) * R, Math.sin(a) * R, b.bright ? 1.7 : 1, 0, 7);
        ctx.fillStyle = b.bright ? 'rgba(240,117,117,.45)' : 'rgba(240,117,117,.16)';
        ctx.fill();
      }

      ctx.lineCap = 'round';
      // raízes — discretas
      for (const s of roots) {
        ctx.lineWidth = Math.max(0.6, (s.d / ROOT_D) * 2.2);
        ctx.strokeStyle = lerpHex('#59110b', '#8c4a3a', 1 - s.d / ROOT_D)
          .replace('rgb', 'rgba')
          .replace(')', ',0.55)');
        ctx.beginPath();
        ctx.moveTo(s.x1, s.y1);
        ctx.lineTo(s.x2, s.y2);
        ctx.stroke();
      }
      // copa — gradiente tronco → pontas
      for (const s of branches) {
        const f = 1 - s.d / CANOPY_D;
        ctx.lineWidth = Math.max(0.8, (s.d / CANOPY_D) * 3.6);
        ctx.strokeStyle = lerpHex('#59110b', '#f07575', f);
        ctx.beginPath();
        ctx.moveTo(s.x1, s.y1);
        ctx.lineTo(s.x2, s.y2);
        ctx.stroke();
      }
      // nós de serviço nas pontas — glow + pulso
      for (const n of tips) {
        const pulse = reduce ? 0.85 : 0.5 + 0.5 * Math.sin(t / 720 + n.ph);
        // raiz < normal < hot (galhos vermelhos) — sem ternário aninhado
        let col = '240,117,117';
        let blur = 7;
        let radius = 1.9;
        let alpha = pulse;
        if (n.root) {
          col = '160,90,74';
          blur = 3;
          radius = 1.4;
          alpha = 0.5;
        } else if (n.hot) {
          col = '219,35,40';
          blur = 11;
          radius = 2.6;
        }
        ctx.save();
        ctx.shadowBlur = blur;
        ctx.shadowColor = `rgba(${col},.9)`;
        ctx.beginPath();
        ctx.arc(n.x, n.y, radius, 0, 7);
        ctx.fillStyle = `rgba(${col},${alpha})`;
        ctx.fill();
        ctx.restore();
      }
      ctx.restore();

      if (!reduce) raf = requestAnimationFrame(frame);
    };

    const onResize = () => {
      clearTimeout(rz);
      rz = setTimeout(() => {
        t0 = 0;
        size();
        if (reduce) frame(0);
      }, 150);
    };

    t0 = 0;
    size();
    if (reduce) frame(0);
    else raf = requestAnimationFrame(frame);
    window.addEventListener('resize', onResize);

    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(rz);
      window.removeEventListener('resize', onResize);
    };
  }, []);

  return <canvas ref={ref} className={classes.canvas} aria-hidden="true" />;
};
